// Live (`/c/{slug}/live`, the rail's item; NAV-REDESIGN renamed it from Screens, `/screens`
// redirects; SCREENS-S1, decision 2026-10-28 §4, §7.5; SCREENS-VIEWER; CANVAS-BLOCKS): one page,
// three views — Workspace (the agentic canvas, default), Browser and Computer (the agent screens,
// sessions on the left, the selected one live on the right, and the Accounts tab). Behind the
// `agents` add-on (the mode's feature; `ModeGate` explains when it is off). A company the viewer
// has no access to is refused by the company route (not found) before anything here loads.
import { cn } from "@companyos/ui";
import { createRoute, useNavigate } from "@tanstack/react-router";
import { isLiveView, LEASE_ID, type LiveView, TAB_ID } from "../features/live/live-link";
import { LivePage } from "../features/live/live-page";
import { useCompany } from "../features/use-feature";
import { type FocusSearch, validateFocusSearch } from "../focus/focus";
import { ModeGate } from "../shell/mode-gate";
import { companyRoute } from "./c.$slug";

/**
 * The Live URL (`features/live/live-link.ts` has the scheme):
 *   `?view=workspace|browser|computer` the view (unset: Workspace, or the screens for an older
 *                                     link naming a session, a login or the Accounts tab)
 *   `?session=<lease>`                 the selected screen session; `?lease=` is its alias
 *   `?tab=accounts`                    the Accounts tab (CONNECTIONS-AREA, unchanged); any other
 *                                     `tab` is a browser tab id, passed through
 *   `?login=<account>`                 (Team › Connections › Accounts › Log in) opens that
 *                                     account's login browser here, once
 */
export type ScreensSearch = FocusSearch & {
  view?: LiveView;
  session?: string;
  lease?: string;
  tab?: string;
  login?: string;
};

export function validateScreensSearch(search: Record<string, unknown>): ScreensSearch {
  const out: ScreensSearch = validateFocusSearch(search);
  if (isLiveView(search.view)) out.view = search.view;
  if (typeof search.session === "string" && LEASE_ID.test(search.session))
    out.session = search.session;
  if (typeof search.lease === "string" && LEASE_ID.test(search.lease)) out.lease = search.lease;
  if (typeof search.tab === "string" && TAB_ID.test(search.tab)) out.tab = search.tab;
  if (typeof search.login === "string" && LEASE_ID.test(search.login)) out.login = search.login;
  return out;
}

export const screensRoute = createRoute({
  getParentRoute: () => companyRoute,
  // NAV-REDESIGN: Live (was Screens, `/screens` redirects: c.$slug.legacy.tsx).
  path: "live",
  validateSearch: validateScreensSearch,
  component: function Screens() {
    const { api } = screensRoute.useRouteContext();
    const company = useCompany();
    const search = screensRoute.useSearch();
    const navigate = useNavigate({ from: screensRoute.fullPath });
    // The page fills the main area's height: the mode root is a flex column all the way down
    // (a plain block there stopped the sessions list and the viewer at their content height).
    return (
      <div
        data-slot="screens-fill"
        className={cn(
          "h-full",
          "[&>[data-mode-root]]:flex [&>[data-mode-root]]:h-full [&>[data-mode-root]]:min-h-0 [&>[data-mode-root]]:flex-col",
        )}
      >
        <ModeGate modeKey="live">
          <LivePage
            key={company.id}
            api={api}
            company={company}
            search={search}
            onSearch={(next) =>
              void navigate({ search: (prev: ScreensSearch) => next(prev), replace: true })
            }
          />
        </ModeGate>
      </div>
    );
  },
});
